/* Reading a live map layer.
 *
 * VIDE's off-site program is run out of an ArcGIS layer. That layer is the
 * accurate copy: whoever holds the GIS updates a school's review status there,
 * and everyone else in the program reads it from there. Importing it would
 * produce a second copy that disagrees with the first inside a week, so this
 * reads it live and never writes to it.
 *
 * Everything here is pure except the one fetch. What comes back is turned into
 * the same shape a tracker has, columns and rows, so the maps, charts, drill
 * panels and tables already in this app work on it without knowing where it
 * came from.
 */

/* Coordinates come back in the layer's own projection unless you ask
   otherwise, and VIDE's schools layer is in NAD83 Puerto Rico and Virgin
   Islands, which is metres. Asking for 4326 is what turns those metres into
   the latitude and longitude a map can use. */
const OUT_SR = "4326";

/* The address of a layer, however it was pasted in.
 *
 * People copy whatever their browser is showing, which is sometimes the
 * service, sometimes the layer, and sometimes a whole query with its own
 * where clause already in it. All three are accepted and all three end up at
 * the same place. */
export function layerUrlOf(raw = "") {
  let url = String(raw).trim().replace(/\s+/g, "");
  if (!url) return null;
  const q = url.toLowerCase().indexOf("/query");
  if (q >= 0) url = url.slice(0, q);
  url = url.replace(/\/+$/, "");
  /* A service address with no layer number on it means the first layer, which
     is the only layer on every service this program uses. */
  if (/\/(FeatureServer|MapServer)$/i.test(url)) url += "/0";
  if (!/\/(FeatureServer|MapServer)\/\d+$/i.test(url)) return null;
  return url;
}

/* A where clause already written into a pasted query URL, so somebody who
   pastes the whole thing does not lose the filter they had. */
export function whereIn(raw = "") {
  const m = /[?&]where=([^&]*)/i.exec(String(raw));
  if (!m) return null;
  const value = decodeURIComponent(m[1].replace(/\+/g, " ")).trim();
  return value && value !== "1=1" ? value : null;
}

/* How many features to ask for at once.
 *
 * A service has its own ceiling, and it does not tell you it has stopped: ask
 * for everything from a layer capped at two thousand and you get two thousand
 * features and a perfectly successful response. The schools layer has
 * sixty four so it never showed, and a layer of site photographs would have
 * shown it in the worst way, as a map that looked complete and was not.
 *
 * So pages are asked for explicitly, and a full page is treated as proof that
 * there is more rather than as the end. */
export const PAGE = 1000;

export function queryUrl(raw, where = "1=1", { offset = null, limit = null } = {}) {
  const layer = layerUrlOf(raw);
  if (!layer) return null;
  const params = new URLSearchParams({
    where: where || "1=1",
    outFields: "*",
    outSR: OUT_SR,
    f: "geojson",
  });
  if (offset !== null) params.set("resultOffset", String(offset));
  if (limit !== null) params.set("resultRecordCount", String(limit));
  return `${layer}/query?${params.toString()}`;
}

/* ArcGIS answers a bad request with a 200 and an error object in the body, so
   the status code alone proves nothing. */
export function readError(payload) {
  if (!payload || typeof payload !== "object") return "The layer did not return map data.";
  if (payload.error) {
    const e = payload.error;
    const detail = Array.isArray(e.details) ? e.details.filter(Boolean).join(" ") : "";
    if (e.code === 499 || e.code === 498 || /token/i.test(e.message ?? "")) {
      return "That layer needs a sign in. The app can only read layers the client has shared publicly.";
    }
    return [e.message, detail].filter(Boolean).join(" ") || "The layer refused the request.";
  }
  if (payload.type !== "FeatureCollection") return "That address did not return map features.";
  return null;
}

async function askOnce(url, fetchImpl) {
  let response;
  try {
    response = await fetchImpl(url);
  } catch {
    /* A browser reports a blocked cross-origin read and a dead network the
       same way, so this says both rather than guessing. */
    throw new Error("Could not reach that layer. Either this network blocks it or the layer is not shared publicly.");
  }
  if (!response.ok) throw new Error(`The layer answered ${response.status}.`);
  const payload = await response.json();
  const problem = readError(payload);
  if (problem) throw new Error(problem);
  return payload;
}

/* Everything in the layer, however many pages that takes.
 *
 * The stop is a page that comes back short. exceededTransferLimit is honoured
 * where a service sends it, but it is not sent by every service and a screen
 * that trusted it would silently draw a partial map on the ones that do not.
 *
 * The cap is a guard against a layer far larger than this app should be
 * drawing, and it is reported rather than hidden: a map missing half its
 * points has to say so. */
export async function fetchFeatures(raw, where = "1=1", {
  fetchImpl = fetch, page = PAGE, cap = 20000,
} = {}) {
  if (!layerUrlOf(raw)) {
    throw new Error("That does not look like a feature layer address. It should end in FeatureServer or FeatureServer/0.");
  }

  const first = await askOnce(queryUrl(raw, where, { offset: 0, limit: page }), fetchImpl);
  const features = [...(first.features ?? [])];
  let more = first.exceededTransferLimit === true || features.length >= page;

  while (more && features.length < cap) {
    const next = await askOnce(
      queryUrl(raw, where, { offset: features.length, limit: page }), fetchImpl);
    const batch = next.features ?? [];
    if (!batch.length) { more = false; break; }
    features.push(...batch);
    more = batch.length >= page;
  }

  return { ...first, features, truncated: more };
}

/* ------------------------------------------------------- shaping the rows */

const looksLikeDate = (v) =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(v.trim());

const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

/* A column's type, from what is actually in it.
 *
 * Choice rather than text when the values are a short closed list, because a
 * closed list draws as chips and opens as a chart, and a free text column
 * does neither. */
function typeOf(values) {
  const filled = values.filter((v) => !isBlank(v));
  if (!filled.length) return "text";
  if (filled.every((v) => typeof v === "number" && Number.isFinite(v))) return "number";
  if (filled.every(looksLikeDate)) return "date";
  const distinct = new Set(filled.map((v) => String(v).trim()));
  const short = [...distinct].every((v) => v.length <= 40);
  if (short && distinct.size <= 12 && distinct.size < filled.length) return "choice";
  return "text";
}

/* Field names as they come out of a GIS are shouted and abbreviated. This is
   only about how a header reads on screen: the key stays exactly as the layer
   sent it, so nothing downstream has to guess. */
export function humanise(name = "") {
  const spaced = String(name)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    /* RECStatus is an abbreviation with a word stuck to it, and splitting on
       the last capital of the run is what turns it into REC Status rather
       than R E C Status. */
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  if (!spaced) return String(name);
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/* Longitude first, then latitude. That is the GeoJSON order and it is the
   opposite of the way anybody says it out loud, which is why it is written
   down here rather than remembered. */
function pointOf(geometry) {
  if (!geometry || geometry.type !== "Point") return null;
  const [lon, lat] = geometry.coordinates ?? [];
  if (typeof lat !== "number" || typeof lon !== "number") return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}

export const LAT_KEY = "_lat";
export const LON_KEY = "_lon";

/* A feature collection as columns and rows.
 *
 * Latitude and longitude are added as their own columns because that is how
 * every map in this app finds its points, and because a layer's own X and Y
 * fields are whatever projection the layer was built in. Here they are always
 * degrees.
 */
export function featuresToTable(payload, { labels = {} } = {}) {
  const features = Array.isArray(payload?.features) ? payload.features : [];

  const keys = [];
  const seen = new Set();
  for (const f of features) {
    for (const k of Object.keys(f?.properties ?? {})) {
      if (!seen.has(k)) { seen.add(k); keys.push(k); }
    }
  }

  const rows = features.map((f, i) => {
    const point = pointOf(f.geometry);
    const row = { ...(f.properties ?? {}) };
    row._id = String(f.id ?? f.properties?.OBJECTID ?? f.properties?.FID ?? `f-${i}`);
    row._index = i;
    row[LAT_KEY] = point ? point.lat : null;
    row[LON_KEY] = point ? point.lon : null;
    return row;
  });

  const columns = [
    ...keys.map((key) => ({
      key,
      label: labels[key] ?? humanise(key),
      type: typeOf(rows.map((r) => r[key])),
    })),
    { key: LAT_KEY, label: "Latitude", type: "number" },
    { key: LON_KEY, label: "Longitude", type: "number" },
  ];

  for (const c of columns) {
    if (c.type !== "choice") continue;
    c.options = [...new Set(rows.map((r) => r[c.key]).filter((v) => !isBlank(v))
      .map((v) => String(v).trim()))].sort();
  }

  return { columns, rows, placed: rows.filter((r) => r[LAT_KEY] !== null).length };
}

/* --------------------------------------------------------- photographs */

/* Some layers carry their photographs as attached files rather than as a URL
   in a field. The risk photo layer is one: geotagged pictures walked off a
   phone, one or more hung off each point. They are fetched separately from the
   features, which is why this is its own trip rather than more columns.
 */
export const layerInfoUrl = (raw) => {
  const layer = layerUrlOf(raw);
  return layer ? `${layer}?f=json` : null;
};

/* One request for the attachments of many records, rather than one request per
   record. A layer that does not support it says so and the caller falls back. */
export const attachmentsUrl = (raw, objectIds = []) => {
  const layer = layerUrlOf(raw);
  if (!layer || !objectIds.length) return null;
  const params = new URLSearchParams({
    objectIds: objectIds.join(","),
    returnUrl: "true",
    f: "json",
  });
  return `${layer}/queryAttachments?${params.toString()}`;
};

/* Where the file itself lives. Handed to an img tag, so the browser fetches it
   from the service directly and this app never holds a copy. */
export const attachmentUrl = (raw, objectId, attachmentId) => {
  const layer = layerUrlOf(raw);
  return layer ? `${layer}/${objectId}/attachments/${attachmentId}` : null;
};

const IMAGE = /^image\//i;

/* Whether a layer has files attached to its records at all, so a screen only
   goes looking when there is something to find. */
export async function fetchLayerInfo(raw, { fetchImpl = fetch } = {}) {
  const url = layerInfoUrl(raw);
  if (!url) return null;
  try {
    const response = await fetchImpl(url);
    if (!response.ok) return null;
    const info = await response.json();
    if (info?.error) return null;
    return {
      hasAttachments: info.hasAttachments === true,
      name: info.name ?? null,
      displayField: info.displayField ?? null,
      maxRecordCount: info.maxRecordCount ?? null,
    };
  } catch {
    return null;
  }
}

/* The pictures attached to a set of records, keyed by the record they hang off.
 *
 * Only images. An attachment can be anything somebody dragged in, a PDF or a
 * spreadsheet, and a grid of broken image frames is worse than none.
 */
export async function fetchAttachments(raw, objectIds = [], {
  fetchImpl = fetch, batch = 50,
} = {}) {
  const byRecord = new Map();
  const ids = objectIds.filter((id) => id !== null && id !== undefined);
  if (!ids.length) return byRecord;

  for (let from = 0; from < ids.length; from += batch) {
    const slice = ids.slice(from, from + batch);
    const url = attachmentsUrl(raw, slice);
    if (!url) break;
    let payload;
    try {
      const response = await fetchImpl(url);
      if (!response.ok) break;
      payload = await response.json();
    } catch {
      break;
    }
    if (payload?.error) break;

    for (const group of payload?.attachmentGroups ?? []) {
      const parent = group.parentObjectId;
      const pictures = (group.attachmentInfos ?? [])
        .filter((a) => IMAGE.test(a.contentType ?? ""))
        .map((a) => ({
          id: a.id,
          name: a.name ?? "",
          size: a.size ?? null,
          contentType: a.contentType,
          url: a.url ?? attachmentUrl(raw, parent, a.id),
        }));
      if (pictures.length) byRecord.set(parent, pictures);
    }
  }

  return byRecord;
}
